import { AfterViewInit, Component, EventEmitter, Input, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Sort } from '@angular/material/sort';
import { Router } from '@angular/router';
import { BaseComponent } from 'src/app/core/base.component';
import { AlertModalComponent } from 'src/app/shared/modals/alert-modal/alert-modal.component';
import { GlobalDataService } from 'src/app/shared/services/global-data.service';
import { SessionService } from 'src/app/shared/services/session.service';
import { Sponsor } from '../sponsor';
import { SponsorList } from '../sponsor-list';
import { SponsorListSummary } from '../sponsor-list-summary';
import { SponsorQueryFilter } from '../sponsor-query-filter';
import { SponsorService } from '../sponsor.service';
import { StringHelpers } from 'src/app/shared/helpers/string-helpers';

@Component({
  selector: 'app-sponsor-list',
  templateUrl: './sponsor-list.component.html',
  styleUrls: ['./sponsor-list.component.scss'],
  standalone: false
})
export class SponsorListComponent extends BaseComponent implements OnInit, AfterViewInit {
  @Input() filter: SponsorQueryFilter = new SponsorQueryFilter();
  public updateFilterEvent = new EventEmitter<any>();
  public sponsors: SponsorList[] = [];
  public summary = new SponsorListSummary();

  constructor(public sessionService: SessionService,
    private globalDataService: GlobalDataService,
    private sponsorService: SponsorService,
    public dialog: MatDialog,
    private router: Router,
    stringHelpers: StringHelpers) {
    super(sessionService, stringHelpers);
    this.displayedColumns = ['name', 'company', 'requestReceipe', 'amount', 'hasPaid', 'type', 'actions']
  }

  ngOnInit(): void {
    if (this.isGlobalOutingSet()) {
      this.selectedIteration = this.getGlobalOutingIteration();
      this.setSearchKey(this.selectedIteration);
      this.getSponsors();
    }
  }

  ngAfterViewInit(): void {
    this.globalDataService.iterationSelectionEvent.subscribe(result => {
      this.selectedIteration = result;
      this.getSponsors();
    })
  }

  addSponsor() {
    this.setSearchKey(this.getGlobalOuting());
    this.router.navigate(['../outing-management/administration/sponsor', 'create', this.searchKey]);
  }

  getSponsors() {
    this.isLoading = true;
    this.filter.outingIterationId = this.selectedIteration.id;
    this.sponsorService.getAllByIteration(this.filter).subscribe(result => {
      this.sponsors = result.results;
      this.filter.totalCount = result.totalCount;
      const totalCount = result.totalCount ?? result.results?.length ?? 0;
      this.updateSummaryTotals(result.results ?? [], totalCount);
      this.updateFilterEvent.emit(result.results);
      this.isLoading = false;
    }, err => {
      this.isLoading = false;
    });
  }

  private updateSummaryTotals(rows: SponsorList[], totalCount: number): void {
    const aggregate = (
      list: SponsorList[]
    ): Pick<SponsorListSummary, 'totalAmount' | 'amountPaid' | 'amountUnpaid'> => {
      let totalAmount = 0;
      let amountPaid = 0;
      let amountUnpaid = 0;
      for (const sponsor of list) {
        const amount = Number(sponsor.amount) || 0;
        totalAmount += amount;
        if (sponsor.hasPaid) {
          amountPaid += amount;
        } else {
          amountUnpaid += amount;
        }
      }
      return { totalAmount, amountPaid, amountUnpaid };
    };

    const applyStats = (stats: Pick<SponsorListSummary, 'totalAmount' | 'amountPaid' | 'amountUnpaid'>): void => {
      this.summary.totalAmount = stats.totalAmount;
      this.summary.amountPaid = stats.amountPaid;
      this.summary.amountUnpaid = stats.amountUnpaid;
    };

    if (!totalCount || rows.length >= totalCount) {
      applyStats(aggregate(rows));
      return;
    }

    const summaryFilter = Object.assign(new SponsorQueryFilter(), this.filter, {
      page: 1,
      pageSize: totalCount
    });
    summaryFilter.outingIterationId = this.selectedIteration.id;
    this.sponsorService.getAllByIteration(summaryFilter).subscribe((full) => {
      applyStats(aggregate(full.results ?? []));
    });
  }

  sortData(sort: Sort) {
    this.filter.sortDirection = sort.direction;
    this.filter.sortColumn = sort.active;
    this.getSponsors();
  }

  resetFilter() {
    this.filter.page = 1;
    this.filter.text = '';
    this.filter.pageSize = 15;
  }

  canEditOrDelete(): boolean {
    return new Date() <= new Date(this.selectedIteration.startDate);;
  }

  delete(sponsor: Sponsor) {
    const dialogRef = this.dialog.open(AlertModalComponent);
    dialogRef.componentInstance.title = 'Delete Sponsor';
    dialogRef.componentInstance.body = `Are you sure you want to delete <strong>"${sponsor.name}"</strong>?`;
    dialogRef.componentInstance.submitText = 'Yes';
    dialogRef.componentInstance.otherText = 'No';
    dialogRef.componentInstance.isOtherTextDisabled = false;
    dialogRef.afterClosed().subscribe((response) => {
      this.isLoading = true;
      if (response === 'Yes') {
        let model = { id: sponsor.id, lastEditedBy: this.getUserIdFromSession() };
        this.sponsorService.delete(sponsor.id, model).subscribe(() => {
          this.getSponsors();
          this.isLoading = false;
        }, err => {
          this.isLoading = false;
        });
      }
      this.isLoading = false;
    });
  }

}
